import React from 'react';
import {
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  IconButton,
  Typography,
  Box,
  Button,
  Stack,
  Tooltip,
  Grid,
} from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DeleteIcon from '@mui/icons-material/Delete';
import { DocumentRef, DocumentStatus } from '../../../../models/core';
import DownloadIcon from '@mui/icons-material/Download';
import { useDocumentTypeQuery } from '../../../../queries/useDocumentTypeQuery';

interface Props {
  files: DocumentRef[];
  usePublicAPI: boolean;
  onDelete?: (doc: DocumentRef) => void;
  onDownload?: (doc: DocumentRef) => Promise<void> | void;
  onUploadToEHR? : (doc:DocumentRef) => Promise<void> | void;
}

const getFileIcon = (type: string) => {
  if (type.startsWith('image/')) return <ImageIcon />;
  if (type === 'application/pdf') return <PictureAsPdfIcon />;
  return <InsertDriveFileIcon />;
};

const DocumentList: React.FC<Props> = ({ files, usePublicAPI, onDelete, onDownload, onUploadToEHR }) => {
  const documentTypesQuery = useDocumentTypeQuery(usePublicAPI);
  const documentTypes = React.useMemo(() => {
    return documentTypesQuery.data || ({} as Record<string, string>);
  }, [documentTypesQuery]);

  return (
    <Box
      sx={{
        width: '100%',
        maxHeight: '100%',
        overflowY: 'auto',
      }}
    >
      <List>
        {files.length === 0 ? (
          <Box minHeight="3em">
            <Typography component="span" variant="body2">
              No Files Currently Available
            </Typography>
          </Box>
        ) : (
          files.map((file) => {
            const emr = file.emr?.find((e) => e.key === '*');

            return (
              <Box
                key={file._id}
                sx={{
                  overflowX: 'auto',
                  minWidth: '100%',
                }}
              >
                <ListItem
                  divider
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    gap: 2,
                  }}
                >
                  <ListItemAvatar>
                    <Avatar>{getFileIcon(file.type)}</Avatar>
                  </ListItemAvatar>

                  <Grid container spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                    <Grid item xs={10}>
                      <Typography variant="body1" noWrap>
                        <b>{file.name}</b>
                      </Typography>
                      <Typography variant="body2" noWrap>
                        <b>Type:</b> {documentTypes[file.documentType || ''] || file.documentType || file.type}
                      </Typography>
                    </Grid>

                    <Grid item xs={2}> 
                      <Stack direction="row" alignItems="top" justifyContent="right" spacing={1.5}>
                        {onDelete && (
                          <Tooltip title="Delete Document">
                            <IconButton edge="end" onClick={() => onDelete(file)}>
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {onDownload && (
                          <Tooltip title="Download Document">
                            <span>
                              <IconButton
                                edge="end"
                                onClick={async () => await onDownload(file)}
                                disabled={!emr || emr.status !== DocumentStatus.VALID}
                              >
                                <DownloadIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {onUploadToEHR && (
                          <Tooltip title="Sync Document">
                            <span>
                              <Button
                                onClick={async () => await onUploadToEHR(file)}
                                sx={{ minWidth: 32 }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    pb: 0.25,
                                  }}
                                >
                                  <Typography variant='body2' color={file.emr.find((e) => e.key === 'zmr') ? 'green' : 'red'}>[EMR]</Typography>
                                </Box>
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    </Grid>

                    <Grid item xs={10}>
                      <Typography variant="body2">
                        <b>Created:</b> {new Date(file.created || '').toLocaleDateString()}
                        {file.creator && (
                          <>
                            <b> by </b>
                            {`${file.creator.name} (${file.creator.id}|${file.creator.ut})`}
                          </>
                        )}
                      </Typography>
                    </Grid>

                    <Grid item xs={2}>
                      <Typography variant="body2" justifyContent='right' textAlign='right'>
                        <b>Status:</b> {emr?.status || DocumentStatus.DELETED}
                      </Typography>
                    </Grid>

                    {file.details?.trim() && (
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                          <b>Notes:</b> {file.details}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </ListItem>
              </Box>
            );
          })
        )}
      </List>
    </Box>
  );
};


export default DocumentList;
